/*
 * NAO-97 T8: stone and pearl size ladder. Port of the signed-off
 * research/2026-09-26-korean-detail-pages/mocks/size-ladder/index.html (diamond and pearl
 * drawing, arrow-key navigation) and the akoya ladder block
 * in mocks/pdp/akoya/index.html (how it sits in the Size chapter, its live line). Contract:
 * top of assets/naomi-pdp.js. Markup and data: snippets/pdp-ladder.liquid.
 *
 * Display only (owner 2026-10-05): every size drawn to scale with its carat and millimetres.
 * No selection, marker or price line; the buy box is the one place a size is chosen.
 */
(() => {
  const f = (n) => n.toFixed(2);
  const pt = (r, deg, cx, cy) => {
    const a = (deg * Math.PI) / 180;
    return [cx + r * Math.cos(a), cy + r * Math.sin(a)];
  };

  const escapeHtml = (s) =>
    String(s).replace(/[&<>"]/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;' })[c]);

  // Round brilliant, top view. Table corners at 22.5 + 45k, star tips at 45k (mocks/size-ladder).
  function diamond(cx, cy, R) {
    const t = 0.555 * R;
    const s = 0.8 * R;
    const V = [];
    const S = [];
    for (let k = 0; k < 8; k++) {
      V.push(pt(t, 22.5 + 45 * k, cx, cy));
      S.push(pt(s, 45 * k, cx, cy));
    }
    let d = `M${V.map((p) => `${f(p[0])} ${f(p[1])}`).join('L')}Z`;
    for (let k = 0; k < 8; k++) {
      const v = V[k];
      const a = S[k];
      const b = S[(k + 1) % 8];
      const g = pt(R, 22.5 + 45 * k, cx, cy);
      d += `M${f(a[0])} ${f(a[1])}L${f(v[0])} ${f(v[1])}L${f(b[0])} ${f(b[1])}`;
      d += `M${f(a[0])} ${f(a[1])}L${f(g[0])} ${f(g[1])}L${f(b[0])} ${f(b[1])}`;
      const gs = pt(R, 45 * k, cx, cy);
      d += `M${f(a[0])} ${f(a[1])}L${f(gs[0])} ${f(gs[1])}`;
    }
    let pav = '';
    if (R > 16) {
      for (let k = 0; k < 8; k++) {
        const v = V[k];
        pav += `M${f(cx)} ${f(cy)}L${f(v[0])} ${f(v[1])}`;
      }
    }
    return (
      `<circle cx="${f(cx)}" cy="${f(cy)}" r="${f(R)}" fill="#FFFFFF" class="pdp-ladder__girdle"/>` +
      (pav ? `<path class="pdp-ladder__pavilion" d="${pav}"/>` : '') +
      `<path class="pdp-ladder__facet" d="${d}"/>`
    );
  }

  function pearl(cx, cy, R) {
    const gx = cx - R * 0.34;
    const gy = cy - R * 0.38;
    return (
      `<circle cx="${f(cx)}" cy="${f(cy)}" r="${f(R)}" fill="url(#pdpLadderPearlBody)"/>` +
      `<circle cx="${f(cx)}" cy="${f(cy)}" r="${f(R)}" fill="url(#pdpLadderPearlRose)"/>` +
      `<ellipse cx="${f(gx)}" cy="${f(gy)}" rx="${f(R * 0.26)}" ry="${f(R * 0.17)}" transform="rotate(-35 ${f(gx)} ${f(gy)})" fill="url(#pdpLadderPearlGlint)" opacity="0.9"/>` +
      `<circle cx="${f(cx)}" cy="${f(cy)}" r="${f(R)}" class="pdp-ladder__pearl-rim"/>`
    );
  }

  function init(block) {
    const dataEl = block.querySelector('[data-pdp-ladder-json]');
    if (!dataEl) return;
    let data;
    try {
      data = JSON.parse(dataEl.textContent);
    } catch {
      return;
    }
    const items = data.items || [];
    const kind = data.kind;
    const ladder = block.querySelector('.pdp-ladder');
    if (!items.length || !ladder) return;

    const MAX_MM = Math.max(...items.map((it) => it.mm));

    function render() {
      ladder.style.setProperty('--n', items.length);
      const colW = ladder.clientWidth / items.length || 1;
      const scale = Math.min((colW * 0.78) / (MAX_MM * 1.06), 10);
      const mTop = 4;
      const maxR = (MAX_MM / 2) * scale * 1.06;
      const H = Math.ceil(mTop + maxR * 2 + 2);
      const W = Math.floor(colW);
      const cy = mTop + maxR;
      const cx = W / 2;
      const size = `width="${W}" height="${H}"`;

      ladder.innerHTML = items
        .map((it) => {
          const R = (it.mm / 2) * scale;
          const art = kind === 'diamond' ? diamond(cx, cy, R) : pearl(cx, cy, R);
          // pearl values are already millimetres ("6.5mm"); stones add theirs under the carat
          const mm = kind === 'diamond' ? `<span class="pdp-ladder__mm">${it.mm.toFixed(1)} mm</span>` : '';
          return `<div class="pdp-ladder__stone" role="listitem">
            <svg ${size} viewBox="0 0 ${W} ${H}" aria-hidden="true" focusable="false">${art}</svg>
            <span class="pdp-ladder__lbl"><span class="pdp-ladder__size">${escapeHtml(it.label)}</span>${mm}</span>
          </div>`;
        })
        .join('');
    }

    // Redraw whenever the ladder's width changes, including 0 to real on first layout
    // (in the integrated page init can run before the chapter is laid out).
    let raf;
    let lastW = ladder.clientWidth;
    new ResizeObserver(() => {
      if (ladder.clientWidth === lastW) return;
      lastW = ladder.clientWidth;
      cancelAnimationFrame(raf);
      raf = requestAnimationFrame(render);
    }).observe(ladder);

    render();
  }

  document.querySelectorAll('[data-pdp-ladder]').forEach(init);
})();
